import {DateUtils} from "./DateUtils";
import {SchemaUtils} from "./SchemaUtils";
import {StringUtils} from "./StringUtils";
import {Component} from "../enum/Component";
import {isValidPhoneNumber} from "libphonenumber-js";

export class ValidationUtil {

    public static validatePage(page: number, appData: any, uiSchema: any, schema: any, excludeRequired: boolean = false) {
        let isRequired = false;
        let isValid = true;
        let pageElements = uiSchema.elements[page].elements;
        for(let pageElement of pageElements){
            let split = pageElement.scope.split('/');
            let pageKey = split[2];
            let componentKey = split[4];
            let typeKey = split[6];

            const rawValue = appData[pageKey][componentKey][typeKey];
            this.trimEmptyStringField(appData, rawValue, pageKey, componentKey, typeKey);
            const value = appData[pageKey][componentKey][typeKey];
            const formElementValue = value;

            const property = schema.properties[pageKey].properties[componentKey];
            if(!excludeRequired){
                const requiredKeysMatch = Array.isArray(property.required) && property.required.includes(typeKey);
                if (requiredKeysMatch && this.isComponentEmpty(appData, formElementValue, componentKey, schema, pageKey,
                    pageElement)){
                    isRequired = true;
                }
            }

            if(ValidationUtil.isPropertyOfType(property, Component.Text) &&
                value !== undefined &&
                !ValidationUtil.isTextComponentValid(property.properties.text,
                    value)){
                    isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Number) &&
                value !== undefined &&
                !ValidationUtil.isNumberComponentValid(property.properties.number,
                    value)){
                    isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Email) &&
                value !== undefined &&
                !ValidationUtil.isEmailComponentValid(property.properties.email,
                    value)){
                    isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Date) &&
                value !== undefined &&
                !ValidationUtil.isDateComponentValid(property.properties.date,
                    value)){
                    isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Time) &&
                value !== undefined &&
                !ValidationUtil.isDateComponentValid(property.properties.time,
                    value)){
                    isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Phone) &&
                value !== undefined &&
                !ValidationUtil.isPhoneComponentValid(property.properties.phone,
                    value)){
                isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Checkbox) &&
                value !== undefined &&
                !ValidationUtil.isCheckboxComponentValid(value, property.properties.checkbox.items.enum)){
                isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Radio) &&
                value !== undefined &&
                !ValidationUtil.isRadioComponentValid(value, property.properties.radio.enum)){
                isValid = false;
            }else if(ValidationUtil.isPropertyOfType(property, Component.Select) &&
                value !== undefined &&
                !ValidationUtil.isSelectComponentValid(value, property.properties.select.enum)){
                isValid = false;
            }
        }
        return {isRequired: isRequired, isValid: isValid};
    };

    public static isCheckboxComponentValid(selectedValues: Array<string>, options: Array<string>): boolean {
        if (!selectedValues || !Array.isArray(selectedValues) || !options || !Array.isArray(options)) {
            return false;
        }
        if(selectedValues.length === 0){
            return true;
        }
        return selectedValues.every(value => options.includes(value));
    }


    public static isRadioComponentValid(selectedValue: any, options: Array<string>): boolean {
        if(selectedValue === ""){
            return true;
        }
        return options.includes(selectedValue);
    }

    public static isSelectComponentValid(selectedValue: string, options: Array<string>): boolean {
        if(selectedValue === ""){
            return true;
        }
        return options.includes(selectedValue);
    }


    public static isTextComponentValid(textComponentProperties: any, textComponentValue: any){
        if(textComponentValue !== undefined &&
            this.isWithinMinMaxTextLengthConstraints(textComponentProperties, textComponentValue)){
            return false;
        }
        return true;
    }

    public static isPhoneComponentValid(phoneComponentProperties: any, phoneComponentValue: any){
        if(phoneComponentValue !== undefined && phoneComponentValue !== '' && !isValidPhoneNumber(phoneComponentValue)){
            return false;
        }
        return true;
    }

    public static isNumberComponentValid(numberComponentProperties: any, numberComponentValue: any){
        if(numberComponentValue !== undefined && numberComponentValue !== '' &&
            this.isWithinMinMaxNumberLengthConstraints(numberComponentProperties, numberComponentValue)){
            return false;
        }
        return true;
    }

    public static isEmailComponentValid(emailComponentProperties: any, emailComponentValue: any){
        if(emailComponentValue !== undefined && emailComponentValue !== '' && (!emailComponentValue.includes('@') ||
            (emailComponentProperties.pattern !== undefined && !(new RegExp(emailComponentProperties.pattern)).test(emailComponentValue)))){
            return false;
        }
        return true;
    }

    public static isDateComponentValid(dateComponentProperties: any, dateComponentValue: any){
        if(dateComponentValue === DateUtils.invalidDateText){
            return false;
        }
        return true;
    }

    public static isPropertyOfType(component: any, type: string) {
        return component.properties[type] !== undefined;
    }

    private static isComponentEmpty(formData: any, formElementValue: any, componentKey: string, schema: any, key: string,
                                    pageElement: any) {
        const componentEnabled = SchemaUtils.isComponentEnabled(pageElement, formData);
        const componentVisible = SchemaUtils.isComponentVisible(pageElement, formData);
        const property = schema.properties[key].properties[componentKey];
        const basicEmptyConditions = (
                formElementValue === undefined ||
                formElementValue === "" ||
                formElementValue === null ||
                (ValidationUtil.isPropertyOfType(property, Component.Checkbox) &&
                    Array.isArray(formElementValue) && formElementValue.length === 0) ||
                (ValidationUtil.isPropertyOfType(property, Component.FileUpload) &&
                    formElementValue.length === 0) ||
                // 0 is not a selectable value on the rating scale (UI range is 1..max),
                // so a 0 from the schema default means "not answered"
                (ValidationUtil.isPropertyOfType(property, Component.Rating) &&
                    Number(formElementValue) === 0)) &&
            componentEnabled &&
            componentVisible;

        // Handle Columns component specifically
        if (ValidationUtil.isPropertyOfType(property, Component.Columns) &&
            componentEnabled &&
            componentVisible) {
            return this.isColumnsComponentEmpty(formElementValue, property);
        }

        return basicEmptyConditions;
    }


    private static isColumnsComponentEmpty(columnsData: any[], columnsSchema: any): boolean {
        if (!columnsData || !Array.isArray(columnsData) || columnsData.length === 0) {
            return true; // Empty if no data
        }

        // Get the inner components schema from the columns schema
        const innerSchema = columnsSchema.properties?.columns?.items?.properties;
        if (!innerSchema) {
            return true; // Empty if no schema found
        }

        // Check each row in the columns data
        for (const row of columnsData) {
            // Check each component in the row
            for (const [componentKey, componentValue] of Object.entries(row)) {
                const componentSchema = innerSchema[componentKey];

                // Check if this inner component is required
                if (componentSchema && componentSchema.required && componentSchema.required.length > 0) {
                    // Extract the actual value from the nested object
                    let actualValue = '';
                    if (componentValue && typeof componentValue === 'object') {
                        const valueKey: string = Object.keys(componentValue)[0];
                        // @ts-ignore
                        actualValue = componentValue[valueKey];
                    }

                    // Check if the required field is empty
                    if (actualValue === undefined || actualValue === "" || actualValue === null) {
                        return true; // empty and required field is provided
                    }
                }
            }
        }

        return false;
    }


    private static trimEmptyStringField(formData: any, formElementValue: any, pageKey: string, componentKey: string,
                                        typeKey: string) {
        if (formElementValue &&
            this.isTrimmedStringFieldEmpty(formElementValue)) {
            formData[pageKey][componentKey][typeKey] = "";
        }
    }

    private static isTrimmedStringFieldEmpty(formElementValue: any) {
        return typeof formElementValue === 'string' &&
            (formElementValue).trim() === "";
    }

    private static isWithinMinMaxTextLengthConstraints(textComponentProperties: any, textComponentValue: string) {
        return (textComponentProperties.minLength !== undefined && String(textComponentValue).length < textComponentProperties.minLength) ||
            (textComponentProperties.maxLength !== undefined && String(textComponentValue).length > textComponentProperties.maxLength);
    }

    private static isWithinMinMaxNumberLengthConstraints(numberComponentProperties: any, numberComponentValue: any) {
        return (numberComponentProperties.minimum !== undefined && Number(numberComponentValue) < numberComponentProperties.minimum) ||
            (numberComponentProperties.maximum !== undefined && Number(numberComponentValue) > numberComponentProperties.maximum);
    }
    public static isEmptyValue(value: any): boolean {
        return (
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
        );
    }
}
